import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Chip,
  Collapse,
  Badge,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  School as SchoolIcon,
  Person as PersonIcon,
  Class as ClassIcon,
  Subject as SubjectIcon,
  EventNote as EventNoteIcon,
  Assessment as AssessmentIcon,
  Payment as PaymentIcon,
  Hotel as HotelIcon,
  PersonAdd as PersonAddIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  AccountCircle as AccountCircleIcon,
  ExpandLess,
  ExpandMore,
  SupervisorAccount as SupervisorAccountIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Schedule as ScheduleIcon,
  Message as MessageIcon,
  CardMembership as CardMembershipIcon,
  Grade as GradeIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { motion } from 'framer-motion';
import axios from 'axios';

const drawerWidth = 280;
const collapsedWidth = 70;

const MainLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [adminMenuOpen, setAdminMenuOpen] = useState(true);
  const [schoolInfo, setSchoolInfo] = useState({
    school_name: 'EMIS School',
    school_logo: null
  });
  const [userProfile, setUserProfile] = useState(null);

  // Fetch school information and user profile
  useEffect(() => {
    const fetchSchoolInfo = async () => {
      try {
        const response = await axios.get('/api/settings/school-info');
        if (response.data.success) {
          setSchoolInfo({
            school_name: response.data.data.school_name || 'EMIS School',
            school_logo: response.data.data.school_logo
          });
        }
      } catch (error) {
        console.error('Error fetching school info:', error);
        // Keep default values on error
        setSchoolInfo({
          school_name: 'EMIS School',
          school_logo: null
        });
      }
    };

    const fetchUserProfile = async () => {
      try {
        const response = await axios.get('profile');
        if (response.data.success) {
          setUserProfile(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    };

    fetchSchoolInfo();
    fetchUserProfile();
  }, []);

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const handleUserMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleUserMenuClose();
    navigate('/login');
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'error';
      case 'teacher': return 'primary';
      case 'student': return 'success';
      case 'parent': return 'warning';
      default: return 'default';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin': return <AdminPanelSettingsIcon />;
      case 'teacher': return <SchoolIcon />;
      case 'student': return <PersonIcon />;
      case 'parent': return <SupervisorAccountIcon />;
      default: return <PersonIcon />;
    }
  };

  const navigationItems = [
    {
      text: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/dashboard',
      roles: ['admin', 'teacher', 'student', 'parent']
    }
  ];

  const adminItems = [
    {
      text: 'User Management',
      icon: <PeopleIcon />,
      path: '/admin/users',
      roles: ['admin']
    },
    {
      text: 'Student Management',
      icon: <PersonIcon />,
      path: '/admin/students',
      roles: ['admin', 'teacher']
    },
    {
      text: 'Teacher Management',
      icon: <SchoolIcon />,
      path: '/admin/teachers',
      roles: ['admin']
    },
    {
      text: 'Class Management',
      icon: <ClassIcon />,
      path: '/admin/classes',
      roles: ['admin', 'teacher']
    },
    {
      text: 'Subject Management',
      icon: <SubjectIcon />,
      path: '/admin/subjects',
      roles: ['admin', 'teacher']
    },
    {
      text: 'Attendance',
      icon: <EventNoteIcon />,
      path: '/admin/attendance',
      roles: ['admin', 'teacher']
    },
    {
      text: 'Exam Management',
      icon: <AssessmentIcon />,
      path: '/admin/exams',
      roles: ['admin', 'teacher']
    },
    {
      text: 'Fee Management',
      icon: <PaymentIcon />,
      path: '/admin/fees',
      roles: ['admin']
    },
    {
      text: 'Hostel Management',
      icon: <HotelIcon />,
      path: '/admin/hostels',
      roles: ['admin']
    },
    {
      text: 'Application Management',
      icon: <PersonAddIcon />,
      path: '/admin/admissions',
      roles: ['admin']
    },
    {
      text: 'Admission Management',
      icon: <SchoolIcon />,
      path: '/admin/admission-management',
      roles: ['admin']
    },
    {
      text: 'System Settings',
      icon: <SettingsIcon />,
      path: '/admin/settings',
      roles: ['admin']
    }
  ];

  const studentItems = [
    {
      text: 'My Dashboard',
      icon: <DashboardIcon />,
      path: '/student/dashboard',
      roles: ['student']
    },
    {
      text: 'My Finance',
      icon: <PaymentIcon />,
      path: '/student/finance',
      roles: ['student']
    },
    {
      text: 'Exam Results',
      icon: <AssessmentIcon />,
      path: '/student/exam-results',
      roles: ['student']
    },
    {
      text: 'Class Schedule',
      icon: <ScheduleIcon />,
      path: '/student/schedule',
      roles: ['student']
    },
    {
      text: 'My Teachers',
      icon: <SchoolIcon />,
      path: '/student/teachers',
      roles: ['student']
    },
    {
      text: 'Messages',
      icon: <MessageIcon />,
      path: '/student/messages',
      roles: ['student']
    },
    {
      text: 'ID Card',
      icon: <CardMembershipIcon />,
      path: '/student/id-card',
      roles: ['student']
    },
    {
      text: 'Grade Sheets',
      icon: <GradeIcon />,
      path: '/student/exam-results',
      roles: ['student']
    }
  ];

  const hasAccess = (roles) => {
    return roles.includes(user?.role);
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Top App Bar */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="toggle drawer"
            onClick={handleDrawerToggle}
            edge="start"
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            {schoolInfo.school_logo ? (
              <img
                src={`http://localhost:3000${schoolInfo.school_logo}`}
                alt="School Logo"
                style={{
                  height: 40,
                  width: 40,
                  objectFit: 'contain',
                  marginRight: 16,
                  borderRadius: 4
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'inline-block';
                }}
              />
            ) : null}
            <SchoolIcon 
              sx={{ 
                mr: 2, 
                fontSize: 32,
                display: schoolInfo.school_logo ? 'none' : 'inline-block'
              }} 
            />
            <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 'bold' }}>
              EMIS --- {schoolInfo.school_name}
            </Typography>
          </Box>

          {/* User Menu */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              icon={getRoleIcon(user?.role)}
              label={user?.role?.toUpperCase()}
              color={getRoleColor(user?.role)}
              size="small"
              sx={{ 
                fontWeight: 'bold',
                color: 'white',
                '& .MuiChip-icon': { color: 'white' }
              }}
            />
            
            <Tooltip title="User Menu">
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="user-menu"
                aria-haspopup="true"
                onClick={handleUserMenuOpen}
                color="inherit"
                sx={{ p: 0 }}
              >
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: userProfile?.profile_picture ? 'transparent' : 'rgba(255,255,255,0.2)',
                    border: '2px solid rgba(255,255,255,0.3)',
                    fontWeight: 'bold'
                  }}
                  src={userProfile?.profile_picture ? `http://localhost:3000/uploads/profiles/${userProfile.profile_picture}` : undefined}
                >
                  {!userProfile?.profile_picture && getInitials(userProfile?.first_name && userProfile?.last_name 
                    ? `${userProfile.first_name} ${userProfile.last_name}` 
                    : user?.username)}
                </Avatar>
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      {/* User Menu Dropdown */}
      <Menu
        id="user-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleUserMenuClose}
        PaperProps={{
          sx: {
            mt: 1.5,
            minWidth: 220,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            borderRadius: 2,
          }
        }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Avatar
              sx={{
                width: 48,
                height: 48,
                mr: 2,
                bgcolor: userProfile?.profile_picture ? 'transparent' : 'primary.main'
              }}
              src={userProfile?.profile_picture ? `http://localhost:3000/uploads/profiles/${userProfile.profile_picture}` : undefined}
            >
              {!userProfile?.profile_picture && getInitials(userProfile?.first_name && userProfile?.last_name 
                ? `${userProfile.first_name} ${userProfile.last_name}` 
                : user?.username)}
            </Avatar>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                {userProfile?.first_name && userProfile?.last_name 
                  ? `${userProfile.first_name} ${userProfile.last_name}` 
                  : user?.username}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {userProfile?.email || user?.email}
              </Typography>
            </Box>
          </Box>
          <Chip
            icon={getRoleIcon(user?.role)}
            label={user?.role?.toUpperCase()}
            color={getRoleColor(user?.role)}
            size="small"
            sx={{ fontWeight: 'bold' }}
          />
        </Box>
        
        <MenuItem onClick={() => { handleNavigation('/profile'); handleUserMenuClose(); }}>
          <ListItemIcon>
            <AccountCircleIcon fontSize="small" />
          </ListItemIcon>
          My Profile
        </MenuItem>
        
        <Divider />
        
        <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" color="error" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>

      {/* Left Sidebar */}
      <Drawer
        variant="permanent"
        sx={{
          width: open ? drawerWidth : collapsedWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: open ? drawerWidth : collapsedWidth,
            boxSizing: 'border-box',
            transition: 'width 0.3s ease',
            overflowX: 'hidden',
            background: 'linear-gradient(180deg, #f8f9fa 0%, #ffffff 100%)',
            borderRight: '1px solid #e0e0e0',
            boxShadow: open ? '4px 0 20px rgba(0,0,0,0.05)' : 'none',
          },
        }}
      >
        <Toolbar />
        
        <Box sx={{ overflow: 'auto', flex: 1 }}>
          <List>
            {/* Main Navigation */}
            {navigationItems.map((item) => (
              hasAccess(item.roles) && (
                <motion.div
                  key={item.text}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => handleNavigation(item.path)}
                      selected={isActive(item.path)}
                      sx={{
                        minHeight: 48,
                        justifyContent: open ? 'initial' : 'center',
                        px: 2.5,
                        mx: 1,
                        mb: 0.5,
                        borderRadius: 2,
                        '&.Mui-selected': {
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                          },
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(102, 126, 234, 0.08)',
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: open ? 3 : 'auto',
                          justifyContent: 'center',
                          color: isActive(item.path) ? 'white' : 'inherit',
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText 
                        primary={item.text} 
                        sx={{ 
                          opacity: open ? 1 : 0,
                          '& .MuiListItemText-primary': {
                            fontWeight: isActive(item.path) ? 'bold' : 'normal',
                          }
                        }} 
                      />
                    </ListItemButton>
                  </ListItem>
                </motion.div>
              )
            ))}

            {/* Admin Section */}
            {hasAccess(['admin', 'teacher']) && (
              <>
                <Divider sx={{ my: 1, mx: 2 }} />
                
                {open && (
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => setAdminMenuOpen(!adminMenuOpen)}
                      sx={{
                        minHeight: 48,
                        px: 2.5,
                        mx: 1,
                        borderRadius: 2,
                        '&:hover': {
                          backgroundColor: 'rgba(102, 126, 234, 0.08)',
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 0, mr: 3, justifyContent: 'center' }}>
                        <AdminPanelSettingsIcon />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Administration" 
                        sx={{ 
                          '& .MuiListItemText-primary': {
                            fontWeight: 'bold',
                            color: '#667eea'
                          }
                        }} 
                      />
                      {adminMenuOpen ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                  </ListItem>
                )}

                <Collapse in={adminMenuOpen && open} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {adminItems.map((item) => (
                      hasAccess(item.roles) && (
                        <motion.div
                          key={item.text}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <ListItem disablePadding>
                            <ListItemButton
                              onClick={() => handleNavigation(item.path)}
                              selected={isActive(item.path)}
                              sx={{
                                minHeight: 44,
                                justifyContent: open ? 'initial' : 'center',
                                px: 2.5,
                                mx: 1,
                                mb: 0.5,
                                ml: 2,
                                borderRadius: 2,
                                '&.Mui-selected': {
                                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                  color: 'white',
                                  '&:hover': {
                                    background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                                  },
                                },
                                '&:hover': {
                                  backgroundColor: 'rgba(102, 126, 234, 0.08)',
                                },
                              }}
                            >
                              <ListItemIcon
                                sx={{
                                  minWidth: 0,
                                  mr: open ? 3 : 'auto',
                                  justifyContent: 'center',
                                  color: isActive(item.path) ? 'white' : 'inherit',
                                }}
                              >
                                {item.icon}
                              </ListItemIcon>
                              <ListItemText 
                                primary={item.text} 
                                sx={{ 
                                  opacity: open ? 1 : 0,
                                  '& .MuiListItemText-primary': {
                                    fontWeight: isActive(item.path) ? 'bold' : 'normal',
                                    fontSize: '0.9rem'
                                  }
                                }} 
                              />
                            </ListItemButton>
                          </ListItem>
                        </motion.div>
                      )
                    ))}
                  </List>
                </Collapse>

                {/* Show admin items when collapsed */}
                {!open && adminItems.map((item) => (
                  hasAccess(item.roles) && (
                    <motion.div
                      key={item.text}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <ListItem disablePadding>
                        <Tooltip title={item.text} placement="right">
                          <ListItemButton
                            onClick={() => handleNavigation(item.path)}
                            selected={isActive(item.path)}
                            sx={{
                              minHeight: 48,
                              justifyContent: 'center',
                              px: 2.5,
                              mx: 1,
                              mb: 0.5,
                              borderRadius: 2,
                              '&.Mui-selected': {
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                color: 'white',
                              },
                              '&:hover': {
                                backgroundColor: 'rgba(102, 126, 234, 0.08)',
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: 0,
                                justifyContent: 'center',
                                color: isActive(item.path) ? 'white' : 'inherit',
                              }}
                            >
                              {item.icon}
                            </ListItemIcon>
                          </ListItemButton>
                        </Tooltip>
                      </ListItem>
                    </motion.div>
                  )
                ))}
              </>
            )}

            {/* Student Section */}
            {hasAccess(['student']) && (
              <>
                <Divider sx={{ my: 1, mx: 2 }} />
                
                {studentItems.map((item) => (
                  <motion.div
                    key={item.text}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <ListItem disablePadding>
                      <ListItemButton
                        onClick={() => handleNavigation(item.path)}
                        selected={isActive(item.path)}
                        sx={{
                          minHeight: 48,
                          justifyContent: open ? 'initial' : 'center',
                          px: 2.5,
                          mx: 1,
                          mb: 0.5,
                          borderRadius: 2,
                          '&.Mui-selected': {
                            background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
                            color: 'white',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #45a049 0%, #3d8b40 100%)',
                            },
                          },
                          '&:hover': {
                            backgroundColor: 'rgba(76, 175, 80, 0.08)',
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            mr: open ? 3 : 'auto',
                            justifyContent: 'center',
                            color: isActive(item.path) ? 'white' : 'inherit',
                          }}
                        >
                          {open ? item.icon : (
                            <Tooltip title={item.text} placement="right">
                              {item.icon}
                            </Tooltip>
                          )}
                        </ListItemIcon>
                        <ListItemText 
                          primary={item.text} 
                          sx={{ 
                            opacity: open ? 1 : 0,
                            '& .MuiListItemText-primary': {
                              fontWeight: isActive(item.path) ? 'bold' : 'normal',
                            }
                          }} 
                        />
                      </ListItemButton>
                    </ListItem>
                  </motion.div>
                ))}
              </>
            )}
          </List>
        </Box>

        {/* Sidebar Toggle Button */}
        <Box sx={{ p: 1, borderTop: '1px solid #e0e0e0' }}>
          <Tooltip title={open ? "Collapse Sidebar" : "Expand Sidebar"} placement="right">
            <IconButton
              onClick={handleDrawerToggle}
              sx={{
                width: '100%',
                borderRadius: 2,
                '&:hover': {
                  backgroundColor: 'rgba(102, 126, 234, 0.08)',
                },
              }}
            >
              {open ? <ChevronLeftIcon /> : <MenuIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          transition: 'margin 0.3s ease',
          minHeight: '100vh',
          backgroundColor: '#f8f9fa',
        }}
      >
        <Toolbar />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {children}
        </motion.div>
      </Box>
    </Box>
  );
};

export default MainLayout;
